import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router'
import { queryClient } from '../../../../../../App'
import { useIncidentTypes } from '../../../../../../hooks/useIncidentTypes'
import { isAdmin, isDealer } from '../../../../../../services/AccountService'
import { maintenanceEditEquipmentData } from '../../../../../../services/NewApiService'
import { MaintenanceEditEquipmentDataParams } from '../../../../../../types/api.types'
import { BaseOptionType } from '../../../../../SingleRequestPage/components/SingleRequestDrawer/helpers/getFormSchema'
import {
  getDefaultValues,
  getIsBtnDisabled,
} from '../helpers/changeEquipmentData.helpers'

export type ChangeDataFormType = {
  incidentType: BaseOptionType
}

export const useChangeEquipmentData = (
  serviceStore: any,
  serviceOrderId: number,
) => {
  const { pathname } = useLocation()
  const isReportDrawer = pathname.startsWith('/service-reports/')
  const { currentServiceData, updateOrders } = serviceStore
  //   если true показываем инпуты и даем возможность редактировать
  const [isEdit, setIsEdit] = useState(false)
  //   типы "тип инцидента"
  const { data: incedentTypes } = useIncidentTypes()

  const { mutateAsync: updateEquipment, isPending } = useMutation({
    mutationKey: ['update-service-equipment-data'],
    mutationFn: (body: MaintenanceEditEquipmentDataParams) =>
      maintenanceEditEquipmentData(body),
    onSuccess(data) {
      setIsEdit(false)
      if (isAdmin() || isDealer()) {
        queryClient.invalidateQueries({
          queryKey: ['service-table'],
        })
        queryClient.invalidateQueries({
          queryKey: ['get-service-history'],
        })
        serviceStore.updateCurrentServiceData(data)
      } else {
        !isReportDrawer && updateOrders(data) // обновляем данные в таблице заявок
      }
      queryClient.invalidateQueries({
        queryKey: ['single-service-report-table'],
      })

      queryClient.invalidateQueries({
        queryKey: ['get-request-linked-to-service'],
      })
    },
  })

  useEffect(() => {
    if (currentServiceData?.status === 20 && isEdit) setIsEdit(false)
  }, [currentServiceData?.status])

  const defaultValues = getDefaultValues(
    currentServiceData,
    incedentTypes ?? [],
  )

  const { control, watch, reset, handleSubmit } = useForm<ChangeDataFormType>({
    defaultValues,
  })

  // переустанавливаем значения после того как получили опции
  useEffect(() => {
    if (incedentTypes?.length) {
      reset(getDefaultValues(currentServiceData, incedentTypes ?? []))
    }
  }, [incedentTypes?.length, currentServiceData?.incidentType])

  //   все значения
  const values = watch()

  //   проверка изменилось ли какое-нибудь поле(если да то кнопка становится акстивной)
  const isSaveBtnDisabled = getIsBtnDisabled(defaultValues, values) || isPending

  //   на кнопку "отмена"
  const onReject = () => {
    setIsEdit(false)
    reset(getDefaultValues(currentServiceData, incedentTypes ?? []))
  }

  //   на кнопку "применить изменения"
  const onSubmit = async (data: ChangeDataFormType) => {
    const params: Partial<MaintenanceEditEquipmentDataParams> = {
      orderId: serviceOrderId,
      incidentTypeId: data.incidentType.key,
    }

    await updateEquipment(params as MaintenanceEditEquipmentDataParams)
  }

  //   чтобы форма не отправлялась на enter
  const checkKeyDown = (e: any) => {
    if (e.key === 'Enter') e.preventDefault()
  }

  //   на кнопку редактировать
  const handleEdit = () => {
    setIsEdit((prev) => !prev)
  }

  return {
    handleEdit,
    checkKeyDown,
    onSubmit,
    onReject,
    isSaveBtnDisabled,
    control,
    handleSubmit,
    isEdit,
    values,
    incedentTypes,
  }
}
